import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import ServiceManager from '../services/ServiceManager';

type QueryOptions = {
    withCache?: boolean;
    gcTime?: number;
    staleTime?: number;
}

type RequestOptions = {
    service: (method: any, argsArray: any) => any;
    apiName: string;
    params: any,
    options?: QueryOptions;
}

export const useCachedAPI = () => {
    const queryClient = useQueryClient();

    /**
     * Generic request handler using React Query's fetchQuery.
     * This replaces the manual cacheKey.current logic.
     */
    const request = useCallback(async ({
        service,
        apiName,
        params,
        options = {},
    } : RequestOptions) => {
        const {
            withCache = false,
            staleTime = 3 * 60 * 1000,
            gcTime = 5 * 60 * 1000,
        } = options;

        // We use the apiName and params as the unique Query Key
        const queryKey = [apiName, params];

        const queryFn = async () => {
            const response = await service(apiName, params);

            return response?.data;
        };

        // fetchQuery returns cached data while it's still fresh (within staleTime)
        // and dedupes concurrent identical requests. When caching is disabled we set
        // staleTime to 0 so the data is always considered stale and refetched.
        return queryClient.fetchQuery({
            queryKey,
            queryFn,
            staleTime: withCache ? staleTime : 0,
            gcTime,
        });
    }, [queryClient]);

    // API Mapping
    const getPortfolioDetails = (params, options?: QueryOptions) => request({
        service: ServiceManager.portfolioManagement, apiName: 'getPortfolioDetails', params, options,
    });

    const getLastApplicationByPortfolio = (params, options?: QueryOptions) => request({
        service: ServiceManager.customInvestmentService, apiName: 'getLastApplicationByPortfolio', params, options,
    });

    const getInvestmentApplication = (params, options?: QueryOptions) => request({
        service: ServiceManager.customInvestmentService, apiName: 'getInvestmentApplication', params, options,
    });

    const getModelPortfolio = (params, options?: QueryOptions) => request({
        service: ServiceManager.portfolioManagement, apiName: 'getModelPortfolio', params, options,
    });

    const getPortfolioProposal = (params, options?: QueryOptions) => request({
        service: ServiceManager.portfolioManagement, apiName: 'getPortfolioProposal', params, options,
    });

    const getWithdrawalData = (params, options?: QueryOptions) => request({
        service: ServiceManager.planService, apiName: 'getWithdrawalData', params, options,
    });

    const getAdvisory = (params, options?: QueryOptions) => request({
        service: ServiceManager.customAdvisoryServices, apiName: 'getAdvisory', params, options,
    });

    return {
        getPortfolioDetails,
        getLastApplicationByPortfolio,
        getInvestmentApplication,
        getModelPortfolio,
        getPortfolioProposal,
        getWithdrawalData,
        getAdvisory,
    };
};
